import React from 'react';
import { Link } from 'react-router-dom';

import { ReactComponent as LinkIcon } from '@assets/svg/link.svg';

import FilesFieldComponent from '@components/common/FilesFieldComponent/FilesFieldComponent';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IFile } from '@model/file';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import type { IEquipmentLink } from '../../model/equipmentLink';
import style from './style.module.scss';

interface IProps {
  equipmentId?: string;
  equipmentName?: string;
  description?: string;
  links?: IEquipmentLink[] | null;
  attachments?: IFile[] | null;
}

const NotesSection: React.FC<IProps> = ({
  equipmentId,
  equipmentName = '',
  description = '',
  links,
  attachments,
}) => {
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();
  const { data: profileData } = useUserProfileQuery();

  const handleOpenLightbox = (event: React.MouseEvent<HTMLElement>): void => {
    const target = event.target;

    if (!(target instanceof HTMLImageElement) || !description) return;

    const images = description.match(/<img [^>]*src="[^"]*"[^>]*>/gm)!.map((x) => {
      return { src: x.replace(/.*src="([^"]*)".*/, '$1') };
    });

    if (images.length) {
      const index = images.findIndex((image) => image.src === target?.currentSrc);

      setActiveModal({ name: MODAL_NAMES.lightboxModal, options: { images, index } });
    }
  };

  const showEmptyBlock = !description && !links && !attachments?.length;

  return (
    <div className={style.notesSection}>
      {showEmptyBlock ? (
        <div className={style.emptyBlock}>
          <div className={style.emptyBlockTitle}>{t('No Information Available')}</div>
          <div className={style.emptyBlockDescr}>
            {t('Add a description or links to provide more details about the equipment.')}
          </div>
          {profileData?.userAccess?.manageEquipmentDeactivation && (
            <div className={style.buttonBlock}>
              <Button
                type="secondary"
                text={t('Edit notes')}
                onClick={() => {
                  setActiveModal({
                    name: MODAL_NAMES.editEquipmentNotesModal,
                    options: { equipmentId, equipmentName, description: '', links: null, files: null },
                  });
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={style.blockHead}>
          <div className={style.blockTitle}>{t('Asset notes')}</div>
          {profileData?.userAccess?.manageEquipmentDeactivation && (
            <Button
              type="secondary"
              text={t('Edit notes')}
              onClick={() => {
                setActiveModal({
                  name: MODAL_NAMES.editEquipmentNotesModal,
                  options: { equipmentId, equipmentName, description, links, files: attachments },
                });
              }}
            />
          )}
        </div>
      )}
      {description && (
        <div>
          <label className={style.fieldLabel}>{t('Description')}</label>
          <div
            className={style.fieldInfo}
            dangerouslySetInnerHTML={{
              __html: description,
            }}
            onClick={(event) => handleOpenLightbox(event)}
          />
        </div>
      )}
      {!!attachments?.length && (
        <FilesFieldComponent files={attachments} attachedFilesClassName={style.attachedFilesWrapper} />
      )}
      {links?.length ? (
        <div>
          <label className={style.fieldLabel}>{t('Links')}</label>
          <div className={style.linksTableWrapper}>
            {links.map((l) =>
              l.link ? (
                <Link key={l.link} className={style.link} to={l.link || ''} target="_blank">
                  {l.name || l.link}
                  <LinkIcon className={style.linkIcon} />
                </Link>
              ) : (
                <div key={l.link} className={style.emptyLink}>
                  {l.name}
                </div>
              ),
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotesSection;
